import { useMemo } from 'react';

import { useSearch } from '@spotify/confidence-core-react';
import {
  SearchIdentityFragment,
  SearchV1ExternalResourceType,
  SearchV1ExternalSearchRequestFiltersIdentityFilterKind,
} from '@spotify/confidence-graphql';

export interface UseIdentitySearchOptions {
  query: string;
  identityKinds?: SearchV1ExternalSearchRequestFiltersIdentityFilterKind[];
  filter?: string;
  pageSize?: number;
  skip?: boolean;
}

export interface UseIdentitySearchResult {
  identities: SearchIdentityFragment[];
  isEmpty: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error?: Error;
  hasNextPage: boolean;
  nextPage: () => void;
  isLoadingNextPage: boolean;
}

export const useIdentitySearch = ({
  query,
  identityKinds,
  filter,
  pageSize = 25,
  skip = false,
}: UseIdentitySearchOptions): UseIdentitySearchResult => {
  const {
    results,
    isEmpty,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    nextPage,
    isLoadingNextPage,
  } = useSearch({
    query,
    types: [SearchV1ExternalResourceType.Identity],
    resourceSpecificFilters:
      identityKinds || filter
        ? {
            identity: {
              kinds: identityKinds,
              filter,
            },
          }
        : undefined,
    pageSize,
    skip,
  });

  const identities = useMemo(() => {
    return results.filter(
      (resource): resource is SearchIdentityFragment =>
        '__typename' in resource && resource.__typename === 'IamV1Identity',
    );
  }, [results]);

  return {
    identities,
    isEmpty,
    isLoading,
    isFetching,
    error,
    hasNextPage,
    nextPage,
    isLoadingNextPage,
  };
};
